/**
 * App entry point: wires the HLS player, the clipping UI, and the
 * HLS-to-MP4 download flow together.
 */

import './style.css';
import { HlsPlayer } from './player';
import {
	convertHlsToMp4,
	downloadMp4Buffer,
	HlsToMp4ConversionError,
} from './converter';

interface Clip {
	id: string;
	url: string;
	start: number;
	end: number;
	createdAt: number;
	/** Whether the source uses Unix-epoch timestamps at capture time. */
	isRelativeToUnixEpoch: boolean;
	abortController: AbortController;
	state: 'ready' | 'working' | 'error';
	errorMessage: string | null;
	progress: number;
	/** DOM node backing this clip, populated when rendered. */
	node: HTMLElement | null;
}

interface ClipDomRefs {
	li: HTMLElement;
	title: HTMLElement;
	meta: HTMLElement;
	status: HTMLElement;
	downloadBtn: HTMLButtonElement;
	removeBtn: HTMLButtonElement;
	progressContainer: HTMLElement;
	progressFill: HTMLElement;
	progressText: HTMLElement;
}

const SAMPLE_URLS = {
	mux: 'https://stream.mux.com/v69RSHhFelSm4701snP22dYz2jICy4E4FUyk02rW4gxRM.m3u8',
	bbb: 'https://test-streams.mux.dev/x36xhzz/x36xhzz.m3u8',
	apple: 'https://devstreaming-cdn.apple.com/videos/streaming/examples/bipbop_16x9/bipbop_16x9_variant.m3u8',
} as const;

const $ = <T extends Element>(selector: string): T => {
	const el = document.querySelector<T>(selector);
	if (!el) throw new Error(`Missing element: ${selector}`);
	return el;
};

// === DOM references ===

const urlForm = $<HTMLFormElement>('#url-form');
const urlInput = $<HTMLInputElement>('#url-input');
const errorElement = $<HTMLParagraphElement>('#error-element');
const warningElement = $<HTMLParagraphElement>('#warning-element');
const loadingElement = $<HTMLParagraphElement>('#loading-element');
const streamStatus = $<HTMLParagraphElement>('#stream-status');
const playerContainer = $<HTMLDivElement>('#player');
const canvas = $<HTMLCanvasElement>('#player-canvas');
const playButton = $<HTMLButtonElement>('#play-button');
const playIcon = $<HTMLElement>('#play-button .icon--play');
const pauseIcon = $<HTMLElement>('#play-button .icon--pause');
const currentTimeEl = $<HTMLElement>('#current-time');
const durationEl = $<HTMLElement>('#duration');
const progressBarContainer = $<HTMLDivElement>('#progress-bar-container');
const progressBar = $<HTMLDivElement>('#progress-bar');
const volumeButton = $<HTMLButtonElement>('#volume-button');
const volumeIcon = $<HTMLElement>('#volume-icon');
const volumeBarContainer = $<HTMLDivElement>('#volume-bar-container');
const volumeBar = $<HTMLDivElement>('#volume-bar');
const volumeCluster = $<HTMLDivElement>('#volume-cluster');
const liveDot = $<HTMLButtonElement>('#live-dot');
const controlsEl = $<HTMLDivElement>('#controls');

const clipLengthInput = $<HTMLInputElement>('#clip-length');
const clipButton = $<HTMLButtonElement>('#clip-button');
const clipButtonLength = $<HTMLElement>('#clip-button-length');
const clipHint = $<HTMLParagraphElement>('#clip-hint');

const clipsList = $<HTMLOListElement>('#clips-list');
const clipsEmpty = $<HTMLParagraphElement>('#clips-empty');
const clipsCount = $<HTMLElement>('#clips-count');
const clipTemplate = $<HTMLTemplateElement>('#clip-item-template');

// === State ===

const clips: Clip[] = [];
let currentUrl: string | null = null;

const player = new HlsPlayer({
	url: '',
	canvas,
	playButton,
	playIcon,
	pauseIcon,
	currentTimeEl,
	durationEl,
	progressBarContainer,
	progressBar,
	volumeButton,
	volumeIcon,
	volumeBarContainer,
	volumeBar,
	volumeCluster,
	liveDot,
	controlsEl,
	playerContainer,
});

player.on('error', ({ message }) => {
	if (message) {
		showError(message);
		streamStatus.dataset.state = 'error';
		streamStatus.textContent = 'Stream error';
	} else {
		hideError();
	}
});

player.on('ready', () => {
	loadingElement.hidden = true;
	clipButton.disabled = false;
	updateClipHint();
	streamStatus.dataset.state = player.getIsLive() ? 'live' : 'ready';
	streamStatus.textContent = player.getIsLive() ? 'Live' : 'Ready';
});

player.on('livechange', ({ isLive }) => {
	streamStatus.dataset.state = isLive ? 'live' : 'ready';
	streamStatus.textContent = isLive ? 'Live' : 'Ready';
});

// === URL form ===

urlForm.addEventListener('submit', (event) => {
	event.preventDefault();
	const url = urlInput.value.trim();
	if (!url) return;
	void loadStream(url);
});

$<HTMLButtonElement>('#sample-mux').addEventListener('click', () => {
	urlInput.value = SAMPLE_URLS.mux;
	void loadStream(SAMPLE_URLS.mux);
});

$<HTMLButtonElement>('#sample-bbb').addEventListener('click', () => {
	urlInput.value = SAMPLE_URLS.bbb;
	void loadStream(SAMPLE_URLS.bbb);
});

$<HTMLButtonElement>('#sample-apple').addEventListener('click', () => {
	urlInput.value = SAMPLE_URLS.apple;
	void loadStream(SAMPLE_URLS.apple);
});

// === Clip controls ===

clipLengthInput.addEventListener('input', () => {
	const length = parseClipLength(clipLengthInput.value);
	clipButtonLength.textContent = length.toString();
});

clipButton.addEventListener('click', () => {
	void captureClip();
});

// === Load stream ===

async function loadStream(url: string): Promise<void> {
	hideError();
	hideWarning();
	loadingElement.hidden = false;
	loadingElement.textContent = 'Loading…';
	streamStatus.dataset.state = 'loading';
	streamStatus.textContent = 'Loading…';
	playerContainer.dataset.state = 'hidden';
	clipButton.disabled = true;
	updateClipHint();

	try {
		currentUrl = url;
		await player.load(url);
	} catch (err) {
		const message = err instanceof Error ? err.message : 'Failed to load stream.';
		showError(message);
		loadingElement.hidden = true;
		streamStatus.dataset.state = 'error';
		streamStatus.textContent = 'Stream error';
		clipButton.disabled = true;
		updateClipHint();
		console.error(err);
	}
}

// === Capture clip ===

async function captureClip(): Promise<void> {
	if (!currentUrl) {
		showError('Load a stream before capturing a clip.');
		return;
	}
	const length = parseClipLength(clipLengthInput.value);
	if (length <= 0) {
		showError('Clip length must be a positive number of seconds.');
		return;
	}
	const currentTime = player.getCurrentTime();
	const liveEdge = player.getLiveEdge();
	const isLive = player.getIsLive();

	// For live streams we anchor `end` to the live edge so the clip is
	// always reachable from the source's DVR window. For VOD we trust
	// the playback head.
	const end = isLive
		? Math.max(currentTime, Math.min(liveEdge, currentTime + length))
		: currentTime;
	const start = end - length;

	if (end - start <= 0.05) {
		showError('The stream has not buffered enough data for that clip yet.');
		return;
	}

	const clip: Clip = {
		id: crypto.randomUUID(),
		url: currentUrl,
		start,
		end,
		createdAt: Date.now(),
		isRelativeToUnixEpoch: player.getIsRelativeToUnixEpoch(),
		abortController: new AbortController(),
		state: 'ready',
		errorMessage: null,
		progress: 0,
		node: null,
	};
	clips.unshift(clip);
	renderClips();
}

// === Download flow ===

async function downloadClip(clip: Clip): Promise<void> {
	if (clip.state === 'working') return;
	clip.state = 'working';
	clip.errorMessage = null;
	clip.progress = 0;
	updateClipNode(clip);
	try {
		const buffer = await convertHlsToMp4({
			url: clip.url,
			start: clip.start,
			end: clip.end,
			signal: clip.abortController.signal,
			onProgress: (progress) => {
				clip.progress = progress;
				updateClipNode(clip);
			},
		});
		clip.state = 'ready';
		clip.progress = 1;
		downloadMp4Buffer(buffer, buildClipFilename(clip));
		updateClipNode(clip);
	} catch (err) {
		if (clip.abortController.signal.aborted) {
			clip.state = 'ready';
			clip.errorMessage = null;
			clip.progress = 0;
		} else {
			clip.state = 'error';
			clip.errorMessage =
				err instanceof HlsToMp4ConversionError
					? err.message
					: err instanceof Error
						? err.message
						: 'Download failed.';
			console.error(err);
		}
		updateClipNode(clip);
	}
}

function cancelClip(clip: Clip): void {
	clip.abortController.abort();
}

function removeClip(clip: Clip): void {
	if (clip.state === 'working') {
		clip.abortController.abort();
	}
	const index = clips.indexOf(clip);
	if (index >= 0) {
		clips.splice(index, 1);
		renderClips();
	}
}

// === Rendering ===

function renderClips(): void {
	clipsList.replaceChildren();
	if (clips.length === 0) {
		clipsList.hidden = true;
		clipsEmpty.hidden = false;
	} else {
		clipsList.hidden = false;
		clipsEmpty.hidden = true;
		for (const clip of clips) {
			clipsList.appendChild(buildClipNode(clip));
		}
	}
	clipsCount.textContent = clips.length.toString();
}

function buildClipNode(clip: Clip): HTMLElement {
	const fragment = clipTemplate.content.cloneNode(true) as DocumentFragment;
	const li = fragment.querySelector('.clip-item') as HTMLElement;
	const refs: ClipDomRefs = {
		li,
		title: li.querySelector('.clip-item__title') as HTMLElement,
		meta: li.querySelector('.clip-item__meta') as HTMLElement,
		status: li.querySelector('.clip-item__status') as HTMLElement,
		downloadBtn: li.querySelector<HTMLButtonElement>('[data-action="download"]')!,
		removeBtn: li.querySelector<HTMLButtonElement>('[data-action="remove"]')!,
		progressContainer: li.querySelector<HTMLElement>('.clip-item__progress')!,
		progressFill: li.querySelector<HTMLElement>('[data-role="progress"]')!,
		progressText: li.querySelector<HTMLElement>('[data-role="progress-text"]')!,
	};
	li.dataset.clipId = clip.id;
	refs.downloadBtn.addEventListener('click', () => {
		if (clip.state === 'working') {
			cancelClip(clip);
		} else {
			void downloadClip(clip);
		}
	});
	refs.removeBtn.addEventListener('click', () => removeClip(clip));
	clip.node = li;
	// Stash refs on the element for cheap incremental updates.
	(li as unknown as { __refs: ClipDomRefs }).__refs = refs;
	updateClipNode(clip);
	return li;
}

function updateClipNode(clip: Clip): void {
	const node = clip.node as (HTMLElement & { __refs?: ClipDomRefs }) | null;
	if (!node || !node.__refs) return;
	const refs = node.__refs;
	const duration = Math.max(0, clip.end - clip.start);
	const timeLabel =
		`${formatTimestamp(clip.start, clip.isRelativeToUnixEpoch)} → `
		+ `${formatTimestamp(clip.end, clip.isRelativeToUnixEpoch)}`;
	refs.title.textContent = `Clip · ${duration.toFixed(1)}s`;
	refs.meta.textContent = timeLabel;
	node.dataset.state = clip.state;
	refs.progressContainer.hidden = clip.state === 'ready' && !clip.errorMessage;
	if (clip.state === 'working') {
		refs.status.textContent = 'Converting to MP4…';
		refs.progressFill.style.width = `${Math.round(clip.progress * 100)}%`;
		refs.progressText.textContent = `${Math.round(clip.progress * 100)}%`;
		refs.downloadBtn.textContent = 'Cancel';
	} else if (clip.state === 'error') {
		refs.status.textContent = clip.errorMessage ?? 'Download failed.';
		refs.downloadBtn.textContent = 'Retry';
	} else {
		refs.status.textContent = 'Ready to download';
		refs.downloadBtn.textContent = 'Download MP4';
	}
}

// === UI helpers ===

function showError(message: string): void {
	if (message) {
		errorElement.textContent = message;
		errorElement.hidden = false;
	} else {
		hideError();
	}
}

function hideError(): void {
	errorElement.textContent = '';
	errorElement.hidden = true;
}

function hideWarning(): void {
	warningElement.textContent = '';
	warningElement.hidden = true;
}

function updateClipHint(): void {
	if (clipButton.disabled) {
		clipHint.textContent = 'Load a stream first, then clip the last few seconds.';
	} else {
		clipHint.textContent = 'Captures the most recent seconds from the playback head.';
	}
}

function parseClipLength(raw: string): number {
	const parsed = Number.parseInt(raw, 10);
	if (Number.isNaN(parsed)) return 30;
	return Math.min(600, Math.max(1, parsed));
}

function formatTimestamp(seconds: number, isUnixEpoch: boolean): string {
	if (isUnixEpoch) {
		const date = new Date(seconds * 1000);
		const pad = (n: number) => n.toString().padStart(2, '0');
		return (
			`${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())} `
			+ `${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}Z`
		);
	}
	const total = Math.max(0, seconds);
	const h = Math.floor(total / 3600);
	const m = Math.floor((total % 3600) / 60);
	const s = Math.floor(total % 60);
	const ms = Math.floor((total - Math.floor(total)) * 1000);
	const pad = (n: number, w = 2) => n.toString().padStart(w, '0');
	if (h > 0) return `${h}:${pad(m)}:${pad(s)}.${pad(ms, 3)}`;
	return `${pad(m)}:${pad(s)}.${pad(ms, 3)}`;
}

function buildClipFilename(clip: Clip): string {
	const date = new Date(clip.createdAt);
	const pad = (n: number) => n.toString().padStart(2, '0');
	const stamp =
		`${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}`
		+ `-${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
	const duration = Math.max(1, Math.round(clip.end - clip.start));
	return `hls-clip-${stamp}-${duration}s.mp4`;
}

// Boot: load the default live stream.
void loadStream(SAMPLE_URLS.mux);
